import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { User, UserRole } from '../generated/gql-types';
import { userActions } from '../user/user.action';
import {
  tenantActions,
  TenantInfo,
  TenantResolutionResult,
} from './tenant.action';
import { upsertOne } from '../util/zustand.util';

interface TenantState {
  tenantInfo: TenantInfo | null;
  tenantResolution: TenantResolutionResult | null;
  tenantUsers: User[];
  isLoading: boolean;
  error: string | null;
}

type TenantStore = TenantState & {
  getCurrentTenantInfo: () => Promise<{
    tenantInfo: TenantInfo | null;
    resolution: TenantResolutionResult | null;
  }>;
  setTenantInfo: (tenantInfo: TenantInfo | null) => void;
  setTenantResolution: (resolution: TenantResolutionResult | null) => void;
  fetchTenantUsersForTenant: (tenantId: string) => Promise<void>;
  fetchTenantUserById: (userId: string) => Promise<User | null>;
  getTenantUserById: (userId: string) => User | null;
  getTenantUsersByRole: (role: UserRole) => User[];
  getTenantUserMapping: () => Record<string, User>;
};

export const useTenantStore = create<TenantStore>()(
  persist(
    (set, get) => ({
      tenantInfo: null,
      tenantResolution: null,
      tenantUsers: [],
      isLoading: false,
      error: null,

      setTenantInfo: (tenantInfo) => set({ tenantInfo }),
      setTenantResolution: (tenantResolution) => set({ tenantResolution }),

      getCurrentTenantInfo: async () => {
        const result = await tenantActions.getCurrentTenantInfo();
        set({
          tenantInfo: result.tenantInfo,
          tenantResolution: result.resolution,
        });
        return result;
      },

      fetchTenantUsersForTenant: async (tenantId: string) => {
        try {
          set({ isLoading: true, error: null });
          const users = await userActions.fetchTenantUsersForTenant(tenantId);
          set({ tenantUsers: users ?? [] });
        } catch (fetchError) {
          console.error('Error fetching tenant users:', fetchError);
          throw fetchError;
        } finally {
          set({ isLoading: false });
        }
      },

      fetchTenantUserById: async (userId: string) => {
        try {
          set({ isLoading: true, error: null });
          const user = await userActions.fetchUserById(userId);
          if (user) {
            set({ tenantUsers: upsertOne(get().tenantUsers, user) });
          } else {
            set({ error: 'User not found' });
          }
          return user;
        } catch (fetchError) {
          console.error('Error fetching tenant user:', fetchError);
          return null;
        } finally {
          set({ isLoading: false });
        }
      },

      getTenantUserById: (userId: string) => {
        return (
          get().tenantUsers?.find(
            (tenantUser: User) => tenantUser?.id === userId,
          ) ?? null
        );
      },

      getTenantUsersByRole: (role: UserRole) => {
        return (
          get().tenantUsers?.filter(
            (tenantUser: User) => tenantUser?.role === role,
          ) ?? []
        );
      },

      getTenantUserMapping: () => {
        return (
          get().tenantUsers?.reduce(
            (accumulator, tenantUser) => {
              accumulator[tenantUser.id] = tenantUser;
              return accumulator;
            },
            {} as Record<string, User>,
          ) ?? {}
        );
      },
    }),
    {
      name: 'tenant-storage',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        tenantInfo: state.tenantInfo,
        tenantResolution: state.tenantResolution,
        tenantUsers: state.tenantUsers,
      }),
    },
  ),
);
